import type FiltersOptions from '../../../../api/typedefs/sdk/PaginationOptions';

import type {
  JobSeeker,
  PhoneNumber,
  ResumeFile,
  TrainingActivity,
  SocialLink,
  ProfileCompletion,
  JobPreference,
  JobSeekerProofFile,
  UserContact,
  ActivationDatafieldsToComplete,
  JobAutoApplySettings,
  UserOngoingPath,
} from '../../../../../index';
import getHelper from '../../../services/getHelper';

import TeamsHelper from './teams/TeamsHelper';
import QuizHelper from './quiz/QuizHelper';
import RecommendedCoursesHelper from './recommendations/RecommendedCoursesHelper';
import RecommendedPathsHelper from './recommendations/RecommendedPathsHelper';
import AvailabilitiesHelper from './availabilities/AvailabilitiesHelper';
import SessionsHelper from './sessions/SessionsHelper';
import SupportRecipientsHelper from './supportRecipients/SupportRecipientsHelper';
import InvoicingParametersHelper from './invoicingParameters/invoicingParameters';
import PlaylistsHelper from './playlists/PlaylistsHelper';

import type {
  Event,
  ProjectPresentationTimeSlot,
  ExpectedUserBenefit,
  UserInterest,
  UserAbout,
  Skill,
} from '../types/User';

import type { Organization } from '../types/Organization';

import type { JobApplication } from '../../job-applications/types';
import type { JobSearchArea } from '../types/JobSearchArea';
import type { JobPosting, UserJobPosting } from '../../job-postings/types';

const URI = '/users/:id';

/**
 * @desc Helper for users.
 */
class UsersHelper extends getHelper(URI, {
  about: getHelper<UserAbout>('/about'),
  addresses: getHelper('/addresses/:id'),
  activationDatafieldsToComplete: getHelper<ActivationDatafieldsToComplete>(
    '/activation-datafields-to-complete'
  ),
  allowances: getHelper('/allowances'),
  apprenticeApplicationPaths: getHelper('/apprentice-application-path'),
  availabilities: AvailabilitiesHelper,
  careerBreaks: getHelper('/career-breaks'),
  careerOutcomes: getHelper('/career-outcomes'),
  declaredSkills: getHelper('/declared-skills'),
  endOfTrainingPermissions: getHelper('/end-of-training-permissions'),
  enrollmentPermissions: getHelper('/enrollment-permissions'),
  followedPathCirrStatus: getHelper('/paths/followed-path/cirr-status/:id'),
  featureInteractions: getHelper('/feature-interactions'),
  followedCourses: getHelper('/followed-courses'),
  insurancePolicies: getHelper('/insurance-policies'),
  invoicingParameters: InvoicingParametersHelper,
  jobApplications: getHelper<JobApplication>('/job-applications'),
  jobAutoApplySettings: getHelper<JobAutoApplySettings>('/job-auto-apply-settings'),
  jobPostings: getHelper<UserJobPosting>('/job-postings/:id'),
  jobSeekers: getHelper<JobSeeker>('/job-seekers/:id', {
    proofFile: getHelper<JobSeekerProofFile>('/proof-files'),
  }),
  jobPreference: getHelper<JobPreference>('/job-preference'),
  jurySessionAssessments: getHelper('/jury-session-assessments'),
  jurySessions: getHelper('/jury-sessions/:id', {
    permissions: getHelper('/permissions'),
  }),
  languageProficiencies: getHelper('/language-proficiencies/:id'),
  links: getHelper('/links'),
  mentorSuggestion: getHelper('/mentor-suggestion'),
  mentoringDatesEligibility: getHelper('/mentoring-dates-eligibility'),
  ongoingPaths: getHelper<UserOngoingPath>('/:id/paths/ongoing'),
  paths: getHelper('/paths/:id', {
    goalAfterGraduation: getHelper('/goal-after-graduation/:id'),
    workAuthorizations: getHelper('/cirr-work-authorizations/:id'),
    cirrSalary: getHelper('/cirr-salary/:id'),
    projects: getHelper('/projects'),
  }),
  phoneNumber: getHelper<PhoneNumber>('/phone-number'),
  playlists: PlaylistsHelper,
  professionalExperiences: getHelper('/professional-experiences/:id'),
  professionalStatus: getHelper('/professional-status'),
  profileCompletion: getHelper<ProfileCompletion>('/profile-completion'),
  projects: getHelper('/projects/:id', {
    sections: getHelper('/sections/:id', {
      milestones: getHelper('/milestones/:id'),
    }),
    links: getHelper('/links'),
    projectSessions: getHelper('/project-sessions'),
    bookPresentation: getHelper('/presentations'),
    follow: getHelper('/follow'),
    unfollow: getHelper('/unfollow'),
    generateCodioLtiSignature: getHelper('/generate-codio-lti-signature'),
    permissions: getHelper('/permissions'),
    securityAudit: getHelper('/security-audit/:id', {
      generateLtiSignature: getHelper('/generate-lti-signature'),
    }),
  }),
  projectSessions: getHelper('/project-sessions/:id', {
    permissions: getHelper('/permissions'),
  }),
  quiz: QuizHelper,
  recommendedCourses: RecommendedCoursesHelper,
  recommendedJobPostings: getHelper<JobPosting>('/recommended-job-postings'),
  recommendedPaths: RecommendedPathsHelper,
  resumeFile: getHelper<ResumeFile>('/resume-file'),
  resumeApprenticeship: getHelper('/resume/apprenticeship'),
  sessions: SessionsHelper,
  sessionTerms: getHelper('/session-terms'),
  socialLinks: getHelper<SocialLink>('/social-links'),
  students: getHelper('/students/:id', {
    projects: getHelper('/projects/:id', {
      permissions: getHelper('/permissions'),
    }),
  }),
  studentStage: getHelper('/student-stage'),
  studentApplications: getHelper('/student-applications/:id'),
  studentApplicationPermissions: getHelper('/student-applications-permissions'),
  studentCredentialPermissions: getHelper('/student-credential-permissions'),
  sessionTermsPermissions: getHelper('/session-terms-permissions'),
  studentCredentials: getHelper('/student-credentials'),
  supports: getHelper('/supports/:id'),
  supportRecipients: SupportRecipientsHelper,
  specificStatuses: getHelper('/specific-statuses'),
  teams: TeamsHelper,
  timeAvailability: getHelper('/time-availability'),
  trainingAccommodations: getHelper('/training-accommodations'),
  trainingPlans: getHelper('/training-plans'),
  trainingActivities: getHelper<TrainingActivity>('/training-activities/:id'),
  trainingPeriods: getHelper('/training-periods/:id'),
  trainingProgramRecommendations: getHelper('/training-programs-recommendation/:id'),
  fundingProgramRecommendations: getHelper('/funding-programs-recommendation/:id'),
  userContacts: getHelper<UserContact>('/user-contacts'),
  videoConferences: getHelper('/video-conferences/:id', {
    reportIssue: getHelper('/report-issue'),
  }),
  workAuthorizationCountries: getHelper('/work-authorization-countries'),
}) {
  /**
   * @desc Get birth information of specific user.
   * @param {Number} userId Id of the user to get birth information.
   * @return {Promise<BirthInformation>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getBirthInformation(userId);
   */
  getBirthInformation(userId: number) {
    const request = super.get(userId, false);
    request.url += '/birth-information';

    return request.execute();
  }

  getEvents(userId: number): Promise<Event[]> {
    const request = super.get(userId, false);
    request.url += '/events';

    return request.execute();
  }

  getProjectPresentationTimeSlots(
    userId: number,
    projectId: number,
    since: string,
    until: string
  ): Promise<ProjectPresentationTimeSlot[]> {
    const request = super.get(userId, false);

    if (since && until) {
      request.url += `/projects/${projectId}/presentation-time-slots?since=${since}&until=${until}`;
    } else {
      request.url += `/projects/${projectId}/presentation-time-slots`;
    }

    return request.execute();
  }

  /**
   * @desc Update birth information of specific user.
   * @param {Number} userId Id of the user to update birth information.
   * @param {BirthInformation} birthInformation New birth information.
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.updateBirthInformation(userId, {
   *    birthDate: '1990-02-23',
   *    cityOfBirth: 'Paris',
   *    countryOfBirth: 'France',
   *    googlePlaceId: 'dlgdg23RFERGF4tg-dsfgd@',
   * });
   */
  updateBirthInformation(userId: number, birthInformation) {
    const request = super.update(userId, birthInformation, false);
    request.url += '/birth-information';

    return request.execute();
  }

  /**
   * @desc Update partially birth information of specific user.
   * @param {Number} userId Id of the user to update birth information.
   * @param {BirthInformation} birthInformation New birth information.
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.partialUpdateBirthInformation(userId, {
   *    birthDate: '1990-02-23',
   * });
   */
  partialUpdateBirthInformation(
    userId: number,
    birthInformation,
    { context }: { context?: string } = {}
  ) {
    const request = super.partialUpdate(userId, birthInformation, false);
    request.url += '/birth-information';
    request.setHeader('X-Request-Context', context);

    return request.execute();
  }

  /**
   * @desc Add interests for a specific user.
   * @param {Number} userId Id of the user to update to add interests.
   * @param {Interest[]} interests New interests to add.
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.addInterests(userId, [{
   *    type: 'path',
   *    id: 42,
   * }]);
   */
  addInterests(
    userId: number,
    interests: UserInterest[],
    queryParams: {},
    context: any
  ): Promise<void> {
    const request = super.create(interests, false);

    request.url += '/interests';
    request.setRouteParam('id', `${userId}`);
    request.setQueryParams(queryParams);
    request.setHeader('X-Request-Context', context);

    return request.execute();
  }

  /**
   * @desc Get interests for a specific user.
   * @param {Number} userId Id of the user to get interests from.
   * @return {Promise<Interest[]>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getInterests(userId);
   */
  getInterests(userId: number): Promise<UserInterest[]> {
    const request = super.get(userId, false);

    request.url += '/interests';
    request.setRouteParam('id', `${userId}`);

    return request.execute();
  }

  /**
   * @desc Get last student path dossier of specific user.
   * @param {Number} userId Id of the user to get last student path dossier.
   * @return {Promise<StudentPathDossier>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getLastStudentPathDossier(userId);
   */
  getLastStudentPathDossier(userId: number) {
    const request = super.get(userId, false);
    request.url += '/last-student-path-dossier';

    return request.execute();
  }

  /**
   * @desc Update partially last student path dossier by providing a "do not assign mentor" date.
   * @param {Number} userId Id of the user to update birth information.
   * @param {string} [doNotAssignMentorDate=null]
   * New "do not assign mentor" date in ISO8601, or `null`.
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user
   *  .users.partialUpdateLastStudentPathDossier(1234, '2017-07-10T18:09:31+0200');
   */
  partialUpdateLastStudentPathDossier(userId: number, doNotAssignMentorDate: string | null = null) {
    const request = super.partialUpdate(userId, { doNotAssignMentorDate }, false);
    request.url += '/last-student-path-dossier';

    return request.execute();
  }

  /**
   * @desc Get privacy settings of specific user.
   * @param {Number} userId Id of the user to get privacy settings.
   * @return {Promise< PrivacySetting >} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getPrivacySettings(userId);
   */
  getPrivacySettings(userId: number) {
    const request = super.get(userId, false);
    request.url += '/privacy-settings';

    return request.execute();
  }

  /**
   * @desc Update partially privacy settings of specific user.
   * @param {Number} userId Id of the user to update privacy settings.
   * @param {PrivacySetting} privacySetting Privacy settings
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.partialUpdatePrivacySettings(userId, {
   *    type: 'share_email_address',
   *    value: true,
   * });
   */
  partialUpdatePrivacySettings(
    userId: number,
    privacySetting,
    { context }: { context?: string } = {}
  ) {
    const request = super.partialUpdate(userId, privacySetting, false);
    request.url += '/privacy-settings';
    request.setHeader('X-Request-Context', context);

    return request.execute();
  }

  /**
   * @desc Get communication settings of specific user.
   * @param {Number} userId Id of the user to get communication settings.
   * @return {Promise< CommunicationSetting >} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getCommunicationSettings(userId);
   */
  getCommunicationSettings(userId: number) {
    const request = super.get(userId, false);
    request.url += '/communication-settings';

    return request.execute();
  }

  /**
   * @desc Update partially communication settings information of specific user.
   * @param {Number} userId Id of the user to update privacy settings.
   * @param {CommunicationSetting} communicationSettings Communication settings
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.partialUpdateCommunicationSettings(userId, {
   *    type: 'newsletter',
   *    value: true,
   * });
   */
  partialUpdateCommunicationSettings(
    userId: number,
    communicationSettings,
    { context }: { context?: string } = {}
  ) {
    const request = super.partialUpdate(userId, communicationSettings, false);
    request.url += '/communication-settings';
    request.setHeader('X-Request-Context', context);

    return request.execute();
  }

  /**
   * @desc Get expected user benefits of specific user.
   * @param {Number} userId Id of the user.
   * @return {Promise<ExpectedUserBenefits>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getUserExpectedBenefits(userId);
   */
  getUserExpectedBenefits(userId: number): Promise<ExpectedUserBenefit[]> {
    const request = super.get(userId, false);
    request.url += '/expected-benefits';

    return request.execute();
  }

  /**
   * @desc Update expected user benefits of specific user.
   * @param {Number} userId Id of the user.
   * @param {Array} benefits Expected user benefits
   * @return {Promise} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.updateUserExpectedBenefits(userId, [
   *   {id: 'benefit-3', comment: 'comment'}
   * ]);
   */
  updateUserExpectedBenefits(userId: number, benefits: ExpectedUserBenefit[]): Promise<void> {
    const request = super.update(userId, benefits, false);
    request.url += '/expected-benefits';

    return request.execute();
  }

  /**
   * @desc Request a new password.
   * @param {String} email of the user to request a new password.
   * @return {Promise} The promise handling the request.
   * @example
   * const email = 'foo@bar.com';
   * const response = await myClient.helpers.user.users.requestNewPassword(email);
   */
  requestNewPassword(email: string): Promise<any> {
    const request = super.create({ email }, false);
    request.url += '/request-new-password';

    return request.execute();
  }

  getJobSearchAreas(userId: number): Promise<JobSearchArea[]> {
    const request = super.get(userId, false);
    request.url += '/job-search-areas';

    return request.execute();
  }

  updateJobSearchAreas(
    userId: number,
    jobSearchAreas: JobSearchArea[],
    { context }: { context?: string } = {}
  ) {
    const request = super.update(userId, jobSearchAreas, false);
    request.url += '/job-search-areas';
    request.setHeader('X-Request-Context', context);
    return request.execute();
  }

  updateLanguageProficiency(
    userId: number,
    language: string,
    level: string,
    { context }: { context: string }
  ) {
    const request = super.update(userId, { level }, false);

    request.url += `/language-proficiencies/${language}`;

    request.setHeader('X-Request-Context', context);

    return request.execute();
  }

  async getOrganization(userId: number): Promise<Organization> {
    const request = super.get(userId, false);
    request.url += '/organization';

    return request.execute();
  }

  async getJobApplications(userId: number, filters: FiltersOptions): Promise<JobApplication> {
    const request = super.find(filters, false).setRouteParam('id', `${userId}`);
    request.url += '/job-applications';

    return request.execute();
  }

  /**
   * @desc Get declared skills of specific user.
   * @param {Number} userId Id of the user.
   * @return {Promise<ExpectedUserDeclaredSkills>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getUserDeclaredSkills(userId);
   */
  getUserDeclaredSkills(userId: number): Promise<Skill[]> {
    const request = super.get(userId, false);
    request.url += '/declared-skills';

    return request.execute();
  }

  /**
     * @desc Update declared skills of specific user.
     * @param {Number} userId Id of the user.
     * @param {Array} skills Declared user skills
     * @return {Promise} The promise handling the request.
     * @example
     * const userId = 1234;
     * const response = await myClient.helpers.user.users.updateUserDeclaredSkills(userId, skills: [
     * {
          "name": "Python",
          "referential": "ROME",
          "position": 1
          }
     * ]);
     */
  updateUserDeclaredSkills(userId: number, skills: Skill[]): Promise<void> {
    const request = super.update(userId, skills, false);

    request.url += '/declared-skills';

    return request.execute();
  }

  /**
   * @desc Get ongoing path of specific user.
   * @param {Number} userId Id of the user.
   * @return {Promise<ExpectedUserOngoingPath>} The promise handling the request.
   * @example
   * const userId = 1234;
   * const response = await myClient.helpers.user.users.getUserOngoingPath(userId);
   */
  getUserOngoingPath(userId: number): Promise<UserOngoingPath> {
    const request = super.get(userId, false);
    request.url += '/paths/ongoing';

    return request.execute();
  }
}

export default UsersHelper;
